import { Link, useNavigate } from '@remix-run/react'
import { StoryblokComponent, renderRichText } from '@storyblok/react'
import { useTranslation } from 'react-i18next'
import SeparatorLine from '#app/components/common/separator-line.tsx'
import renderEmojis from '#app/components/storyblok/emoji-renderer'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyButtonsAndLinks from '#app/components/typography/plenty-buttons-and-links'
import { Icon } from '#app/components/ui/icon'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { useSalesFlags } from '#app/hooks/use-sales-flag'
import {
	type CategoryLinkStoryblok,
	type NavigationCategoriesStoryblok,
} from '#types/component-types-sb'
import CategoriesContent from './categories/categories-content'
import SpecialCategories from './categories/special-categories'
import ScrollableContent from './scrollable-content'
import { SlideContent, SlideTrigger } from './slides'

interface MainContentProps {
	selectedCategory: string | null
	navigationData: NavigationCategoriesStoryblok[]
}

const MainContent = ({
	selectedCategory,
	navigationData,
}: MainContentProps) => {
	const matchedCategory =
		navigationData.find(item => item.title === selectedCategory) ||
		navigationData[0] // Fallback to the first item (e.g., "Women") if no match is found
	let { t } = useTranslation('navbar')
	const navigate = useNavigate()

	const mobileContent = matchedCategory.mobileContent || []
	const smartMobileLinks = matchedCategory.smartMobileLinks || []
	const specialCategories =
		matchedCategory.mobileCategories?.filter(
			category =>
				(category.component === 'parentCategory' ||
					category.component === 'categoryLink') &&
				category.isLarge,
		) || []
	const restOfCategories =
		matchedCategory.mobileCategories?.filter(category => !category.isLarge) ||
		[]

	const { hasWomenSales, hasMenSales, hasKidsSales } = useSalesFlags()

	// Helper to determine the gender based on the category, handling translations
	const getGenderFromCategory = (
		category: string | null,
	): 'women' | 'men' | 'kids' | null => {
		if (!category) return null

		// Normalize the category name to lowercase
		const categoryLower = category.toLowerCase()

		// Check for English names first
		if (categoryLower === 'women' || categoryLower === 'woman') return 'women'
		if (categoryLower === 'men' || categoryLower === 'man') return 'men'
		if (
			categoryLower === 'kids' ||
			categoryLower === 'children' ||
			categoryLower === 'child'
		)
			return 'kids'

		// Check for Danish translations
		if (categoryLower === 'kvinder') return 'women'
		if (categoryLower === 'mænd') return 'men'
		if (categoryLower === 'børn') return 'kids'

		// Try to match against translation keys for additional languages
		const womenTrans = t('women_header').toLowerCase()
		const menTrans = t('men_header').toLowerCase()
		const kidsTrans = t('kids_header').toLowerCase()

		if (categoryLower === womenTrans) return 'women'
		if (categoryLower === menTrans) return 'men'
		if (categoryLower === kidsTrans) return 'kids'

		// If we still can't determine, check for partial matches
		if (categoryLower.includes('women') || categoryLower.includes('kvinder'))
			return 'women'
		if (categoryLower.includes('men') || categoryLower.includes('mænd'))
			return 'men'
		if (
			categoryLower.includes('kid') ||
			categoryLower.includes('børn') ||
			categoryLower.includes('child')
		)
			return 'kids'

		return null
	}

	// Determine if sale link should be shown based on selected category
	const shouldShowSaleLink = () => {
		const gender = getGenderFromCategory(selectedCategory)

		if (gender === 'women') return hasWomenSales
		if (gender === 'men') return hasMenSales
		if (gender === 'kids') return hasKidsSales

		return false
	}

	// Helper to get the proper URL path for the sale link
	const getSalePathForCategory = () => {
		const gender = getGenderFromCategory(selectedCategory)

		if (gender) {
			return `/products-sale/${gender}?sortingOrder=popular`
		}

		// Fallback to the category name as-is if we can't determine the gender
		return `/products-sale/${selectedCategory?.toLowerCase()}?sortingOrder=popular`
	}

	if (!matchedCategory) {
		return (
			<PlentyBody size="sm">No content available for this category</PlentyBody>
		)
	}
	return (
		<>
			<div className="h-fit w-full">
				<nav className="grid w-full gap-6 px-4">
					{shouldShowSaleLink() && (
						<PlentyButtonsAndLinks
							onClick={() => {
								navigate(getSalePathForCategory())
							}}
							as="nav-md"
							className="text-red-90"
						>
							{t('header_sale')}
						</PlentyButtonsAndLinks>
					)}
					{restOfCategories.length > 0 &&
						restOfCategories.map(category => {
							if (category.component === 'categoryLink') {
								return <CategoryLink category={category} key={category._uid} />
							}
							return (
								<div key={category._uid}>
									<SlideTrigger slideKey={category._uid}>
										<div className="flex cursor-pointer items-center justify-between gap-2">
											<PlentyButtonsAndLinks as="nav-md" className="text-black">
												{category.title}
											</PlentyButtonsAndLinks>
											{category?.categories &&
												category.categories.length > 0 && (
													<Icon name="chevron-right-arrow" size="lg" />
												)}
										</div>
									</SlideTrigger>
									<SlideContent slideKey={category._uid}>
										<ScrollableContent className="h-full space-y-3">
											<CategoriesContent
												category={category}
												parentCategory={matchedCategory.title || ''}
											/>
										</ScrollableContent>
									</SlideContent>
								</div>
							)
						})}
				</nav>
				{specialCategories.length > 0 && (
					<div className="grid w-full gap-6 px-4">
						{specialCategories.map(category => {
							if (
								category.component === 'categoryLink' &&
								category.link !== 'DIVIDER'
							) {
								return <CategoryLink category={category} key={category._uid} />
							} else if (
								category.component === 'categoryLink' &&
								category.link === 'DIVIDER'
							) {
								return <SeparatorLine key={`special-${category._uid}`} />
							}

							return (
								category.component === 'parentCategory' && (
									<SpecialCategories
										category={category}
										parentCategory={matchedCategory.title || ''}
										key={`special-${category._uid}`}
									/>
								)
							)
						})}
					</div>
				)}
			</div>

			{mobileContent.map(item => (
				<StoryblokComponent blok={item} key={item._uid} />
			))}
			{smartMobileLinks.length > 0 && (
				<div className="mt-2 grid w-full gap-6 px-4 ">
					{smartMobileLinks.map(category => {
						if (category.component === 'categoryLink') {
							return (
								<CategoryLink
									category={category as CategoryLinkStoryblok}
									key={category._uid}
								/>
							)
						}
						return null
					})}
				</div>
			)}
		</>
	)
}

const CategoryLink = ({ category }: { category: CategoryLinkStoryblok }) => {
	const richTextHTML = renderRichText(category.title)
	const { trackAmplitudeEvent } = useAmplitudeContext()
	return (
		<Link
			className="flex w-full cursor-pointer items-center justify-between gap-2 text-[14px] leading-[22px]"
			to={category.link}
			onClickCapture={() => {
				trackAmplitudeEvent('mobile navbar category link clicked', {
					category: category.title,
					categoryLink: category.link,
				})
			}}
		>
			{renderEmojis(richTextHTML, {})}
		</Link>
	)
}

export default MainContent
